import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Play,
  Pause,
  Clock,
  Mail,
  Users,
  CheckCircle,
} from "lucide-react";
import { API, STAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Sequences({ user }) {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);

  const [newSequence, setNewSequence] = useState({
    name: "",
    description: "",
    steps: [
      { day: 0, template_id: "", action: "send_email" },
      { day: 3, template_id: "", action: "send_email" },
      { day: 7, template_id: "", action: "send_email" },
    ],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [seqRes, tplRes, clientsRes] = await Promise.all([
        fetch(`${API}/sequences`, { credentials: "include" }),
        fetch(`${API}/templates`, { credentials: "include" }),
        fetch(`${API}/clients`, { credentials: "include" }),
      ]);

      if (seqRes.ok) setSequences(await seqRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSequence = async () => {
    if (!newSequence.name.trim()) {
      toast.error("Please enter a sequence name");
      return;
    }

    const validSteps = newSequence.steps.filter((s) => s.template_id);
    if (validSteps.length === 0) {
      toast.error("Please add at least one step with a template");
      return;
    }

    try {
      const response = await fetch(`${API}/sequences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newSequence,
          steps: validSteps,
        }),
      });

      if (response.ok) {
        const created = await response.json();
        setSequences((prev) => [created, ...prev]);
        setShowCreateModal(false);
        setNewSequence({
          name: "",
          description: "",
          steps: [
            { day: 0, template_id: "", action: "send_email" },
            { day: 3, template_id: "", action: "send_email" },
            { day: 7, template_id: "", action: "send_email" },
          ],
        });
        toast.success("Sequence created");
      } else {
        toast.error("Failed to create sequence");
      }
    } catch (error) {
      toast.error("Failed to create sequence");
    }
  };

  const handleAssignSequence = async () => {
    if (selectedClients.length === 0) {
      toast.error("Please select at least one client");
      return;
    }

    try {
      const response = await fetch(
        `${API}/sequences/${selectedSequence.sequence_id}/assign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ client_ids: selectedClients }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(`Sequence assigned to ${result.clients_updated} clients`);
        setShowAssignModal(false);
        setSelectedClients([]);
        fetchData();
      } else {
        toast.error("Failed to assign sequence");
      }
    } catch (error) {
      toast.error("Failed to assign sequence");
    }
  };

  const handleDeleteSequence = async (sequenceId) => {
    if (!window.confirm("Are you sure you want to delete this sequence?"))
      return;

    try {
      const response = await fetch(`${API}/sequences/${sequenceId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setSequences((prev) =>
          prev.filter((s) => s.sequence_id !== sequenceId)
        );
        toast.success("Sequence deleted");
      }
    } catch (error) {
      toast.error("Failed to delete sequence");
    }
  };

  const updateStep = (index, field, value) => {
    setNewSequence((prev) => ({
      ...prev,
      steps: prev.steps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      ),
    }));
  };

  const addStep = () => {
    const lastDay =
      newSequence.steps.length > 0
        ? newSequence.steps[newSequence.steps.length - 1].day
        : 0;
    setNewSequence((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        { day: lastDay + 7, template_id: "", action: "send_email" },
      ],
    }));
  };

  const removeStep = (index) => {
    setNewSequence((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index),
    }));
  };

  const getTemplateName = (templateId) => {
    return (
      templates.find((t) => t.template_id === templateId)?.name || "Unknown"
    );
  };

  // Stats
  const clientsInSequences = clients.filter(
    (c) => c.sequence_status === "active"
  ).length;
  const awaitingResponse = clients.filter(
    (c) => c.sequence_status === "active" && !c.response_received
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sequences-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Email Sequences
          </h1>
          <p className="text-slate-500 mt-1">
            Automated follow-up sequences that pause when clients respond
          </p>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => setShowCreateModal(true)}
          data-testid="create-sequence-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Sequence
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clientsInSequences}</p>
                <p className="text-sm text-slate-500">Clients in Sequences</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{awaitingResponse}</p>
                <p className="text-sm text-slate-500">Awaiting Response</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {clients.filter((c) => c.response_received).length}
                </p>
                <p className="text-sm text-slate-500">Responses Received</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-100">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-slate-900 mb-2">How Sequences Work</h3>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>
              • <strong>Day 0:</strong> First email sent when client is added to
              sequence
            </li>
            <li>
              • <strong>Follow-ups:</strong> Automatic emails sent after X days
              if no response
            </li>
            <li>
              • <strong>Response Received:</strong> Click "Response Received" on
              a client to pause their sequence
            </li>
            <li>
              • <strong>AI Reply:</strong> When you receive a response, paste it
              in and AI will generate your reply
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Sequences List */}
      <div className="space-y-4">
        {sequences.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                No sequences yet. Create your first automated follow-up sequence.
              </p>
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowCreateModal(true)}
              >
                Create Sequence
              </Button>
            </CardContent>
          </Card>
        ) : (
          sequences.map((sequence) => (
            <Card key={sequence.sequence_id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">
                      {sequence.name}
                    </h3>
                    {sequence.description && (
                      <p className="text-sm text-slate-500 mt-1">
                        {sequence.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3">
                      <Badge variant="secondary">
                        {sequence.steps?.length || 0} steps
                      </Badge>
                      <Badge
                        className={
                          sequence.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }
                      >
                        {sequence.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedSequence(sequence);
                        setShowAssignModal(true);
                      }}
                      data-testid={`assign-sequence-${sequence.sequence_id}`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Assign to Clients
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDeleteSequence(sequence.sequence_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Steps Timeline */}
                <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-2">
                  {sequence.steps?.map((step, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 flex-shrink-0"
                    >
                      <div className="bg-slate-100 rounded-lg p-3 min-w-[150px]">
                        <p className="text-xs text-slate-500">Day {step.day}</p>
                        <p className="text-sm font-medium truncate">
                          {getTemplateName(step.template_id)}
                        </p>
                      </div>
                      {idx < sequence.steps.length - 1 && (
                        <div className="w-8 h-0.5 bg-slate-200 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Sequence Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Email Sequence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Sequence Name</Label>
              <Input
                value={newSequence.name}
                onChange={(e) =>
                  setNewSequence({ ...newSequence, name: e.target.value })
                }
                placeholder="e.g., Standard Follow-up Sequence"
                data-testid="sequence-name-input"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newSequence.description}
                onChange={(e) =>
                  setNewSequence({ ...newSequence, description: e.target.value })
                }
                placeholder="What is this sequence for?"
                rows={2}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Sequence Steps</Label>
                <Button variant="outline" size="sm" onClick={addStep}>
                  <Plus className="w-4 h-4 mr-1" /> Add Step
                </Button>
              </div>
              <div className="space-y-3">
                {newSequence.steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="w-20">
                      <Label className="text-xs">Day</Label>
                      <Input
                        type="number"
                        min="0"
                        value={step.day}
                        onChange={(e) =>
                          updateStep(idx, "day", parseInt(e.target.value) || 0)
                        }
                        className="h-9"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Email Template</Label>
                      <Select
                        value={step.template_id}
                        onValueChange={(v) => updateStep(idx, "template_id", v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.template_id} value={t.template_id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {newSequence.steps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 mt-5"
                        onClick={() => removeStep(idx)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreateSequence}
              data-testid="save-sequence-btn"
            >
              Create Sequence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Sequence Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Assign "{selectedSequence?.name}" to Clients
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Select clients to add to this sequence. They will receive automated
              follow-ups until they respond.
            </p>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {clients
                .filter((c) => c.sequence_status !== "active")
                .map((client) => (
                  <label
                    key={client.client_id}
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.client_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClients((prev) => [
                            ...prev,
                            client.client_id,
                          ]);
                        } else {
                          setSelectedClients((prev) =>
                            prev.filter((id) => id !== client.client_id)
                          );
                        }
                      }}
                      className="rounded"
                    />
                    <div>
                      <p className="font-medium text-sm">
                        {client.company_name}
                      </p>
                      <p className="text-xs text-slate-500">{client.stage}</p>
                    </div>
                  </label>
                ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleAssignSequence}
              data-testid="confirm-assign-btn"
            >
              Assign to {selectedClients.length} Client
              {selectedClients.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
